import {
  getCountries,
  getCountryCallingCode,
  isSupportedCountry,
  isValidPhoneNumber,
  parsePhoneNumberFromString,
  type CountryCode as LibCountryCode,
} from "libphonenumber-js/mobile";

import {
  countryFlag,
  countryName,
  DEFAULT_COUNTRY_CODE,
  normalizeCountryCode,
  type CountryCode,
} from "../config/countries.ts";

/**
 * Phone handling for every country libphonenumber has numbering-plan metadata for.
 *
 * Kept separate from a school's country on purpose: a school may be in any ISO 3166-1 country, and its country is
 * used here only as the default region for a number typed without its calling code. A country with no phone
 * metadata (Antarctica, for one) is still a valid school country; its users pick their number's own country.
 *
 * Backed by libphonenumber's own metadata rather than a hand-written table of digit counts: the
 * numbering plans of countries change without warning, and a stale local table fails by
 * rejecting a real customer's number.
 *
 * The `mobile` metadata bundle is used deliberately, not `min`. `min` accepts any valid number,
 * including landlines — and India's `1234567890` is a perfectly valid landline. Sign-in here is by
 * SMS one-time code, so accepting a landline means issuing a code that can never arrive, which
 * looks to the user like the system is broken. `mobile` rejects those up front, verified against a
 * landline and a mobile in each of the original launch countries.
 */

/** A region a phone number can be entered in, from libphonenumber's metadata. */
export type PhoneRegion = {
  alpha2: CountryCode;
  name: string;
  flag: string;
  /** International calling code with its plus, e.g. `+1`. Shared by several regions, so it never identifies one. */
  callingCode: string;
};

/** Every region with numbering-plan metadata, sorted by name. */
export const PHONE_REGIONS: readonly PhoneRegion[] = getCountries()
  .map((alpha2) => ({
    alpha2,
    name: countryName(alpha2),
    flag: countryFlag(alpha2),
    callingCode: `+${getCountryCallingCode(alpha2)}`,
  }))
  .sort((a, b) => a.name.localeCompare(b.name));

/** Whether libphonenumber has numbering-plan metadata for a country. */
export function hasPhoneMetadata(country: string | undefined | null): boolean {
  const normalized = normalizeCountryCode(country);
  return !!normalized && isSupportedCountry(normalized);
}

/** The calling code for a country (`+44`), or null when there is no numbering-plan metadata for it. */
export function callingCodeFor(country: string | undefined | null): string | null {
  const normalized = normalizeCountryCode(country);
  return normalized && isSupportedCountry(normalized)
    ? `+${getCountryCallingCode(normalized)}`
    : null;
}

/**
 * The phone region to default to for a school's country: the country itself when it has metadata, else the
 * platform default. The school's country is not changed by this; only the phone picker's starting point is.
 */
export function phoneRegionOrDefault(country: string | undefined | null): CountryCode {
  const normalized = normalizeCountryCode(country);
  return normalized && isSupportedCountry(normalized) ? normalized : DEFAULT_COUNTRY_CODE;
}

/** Formats a national number for display as the user types, e.g. `98765 43210`. */
export function formatNationalNumber(
  nationalNumber: string,
  country: CountryCode,
): string {
  const digits = nationalNumber.replace(/\D/g, "");
  if (!digits) return "";
  if (!hasPhoneMetadata(country)) return digits;

  const parsed = parsePhoneNumberFromString(digits, country as LibCountryCode);
  return parsed?.formatNational().replace(/^0\s*/, "") ?? digits;
}

/** Strips everything but digits, which is what the input field stores. */
export function toNationalDigits(input: string): string {
  return input.replace(/\D/g, "");
}

/**
 * Validates a national number against its country's numbering plan.
 */
export function isValidNationalNumber(
  nationalNumber: string,
  country: CountryCode,
): boolean {
  const digits = toNationalDigits(nationalNumber);
  if (!digits || !hasPhoneMetadata(country)) return false;

  return isValidPhoneNumber(digits, country as LibCountryCode);
}

/**
 * Converts a national number into the E.164 form the API expects, or null when it is not valid.
 *
 * E.164 is the only shape sent over the wire. The server stores exactly what it is given, so a
 * national number reaching it would be unfindable at the next sign-in from a different country.
 */
export function toE164(nationalNumber: string, country: CountryCode): string | null {
  const digits = toNationalDigits(nationalNumber);
  if (!digits || !hasPhoneMetadata(country)) return null;

  const parsed = parsePhoneNumberFromString(digits, country as LibCountryCode);
  return parsed?.isValid() ? parsed.number : null;
}

/**
 * Keeps what a phone field may hold while typing: digits, with one leading `+`, up to E.164's 15 digits. A `(0)` trunk
 * marker (`+971 (0)50…`) is dropped rather than kept as a digit, as the server does.
 */
export function sanitizePhoneInput(input: string): string {
  const plus = input.trimStart().startsWith("+") ? "+" : "";
  return plus + input.replace(/\(0\)/g, "").replace(/\D/g, "").slice(0, 15);
}

/** Whether a number is written explicitly international: `+…`, or the `00…` international prefix. */
export function isInternationalInput(input: string): boolean {
  const trimmed = input.trim();
  return trimmed.startsWith("+") || trimmed.startsWith("00");
}

/**
 * Reads a phone number typed on a user form at a school in `schoolCountry`, into E.164.
 *
 * - An explicitly international number (`+44 7911 123456`, `0044…`) is read by its own calling code's metadata, at
 *   any school. This is how a school in a country without phone metadata (Antarctica, for one) enters numbers.
 * - Any other number is national, read in the school's country, which needs metadata for that country.
 *
 * The school's country is only the default region here: it never decides whether the school is valid, and a number
 * that carries its own calling code keeps it. Mirrors `PhoneNumber.TryNormalizeInCountry` on the server.
 */
export function toUserPhoneE164(
  input: string,
  schoolCountry: string | undefined | null,
): string | null {
  const trimmed = input.trim();
  if (!trimmed) return null;

  if (isInternationalInput(trimmed)) {
    const digits = trimmed.replace(/^(\+|00)/, "").replace(/\D/g, "");
    const parsed = digits ? parsePhoneNumberFromString(`+${digits}`) : undefined;
    return parsed?.isValid() ? parsed.number : null;
  }

  const country = normalizeCountryCode(schoolCountry);
  return country ? toE164(trimmed, country) : null;
}

/**
 * The validation message for a phone number typed on a user form, or undefined when it can be read. An empty value is
 * the caller's concern (required or optional).
 */
export function userPhoneError(
  input: string,
  schoolCountry: string | undefined | null,
): string | undefined {
  if (!input.trim() || toUserPhoneE164(input, schoolCountry)) return undefined;

  if (isInternationalInput(input)) {
    return "Enter a valid mobile number with its country code, e.g. +44 7911 123456.";
  }

  return hasPhoneMetadata(schoolCountry)
    ? `Enter a valid ${countryName(schoolCountry)} mobile number, or any number with its country code (+…).`
    : "Enter the number with its country code, e.g. +44 7911 123456.";
}

/** Helper text for a phone field at a school in `schoolCountry`. */
export function userPhoneHint(schoolCountry: string | undefined | null): string {
  return hasPhoneMetadata(schoolCountry)
    ? `A mobile number in ${countryName(schoolCountry)}, or any number with its country code (+…).`
    : "Enter the number with its country code, e.g. +44 7911 123456.";
}

/**
 * Splits an E.164 number back into a country and a national number, for display.
 */
export function fromE164(e164: string | undefined | null): {
  country: CountryCode;
  nationalNumber: string;
} {
  if (!e164) {
    return { country: DEFAULT_COUNTRY_CODE, nationalNumber: "" };
  }

  const parsed = parsePhoneNumberFromString(e164);

  if (!parsed?.country) {
    return {
      country: DEFAULT_COUNTRY_CODE,
      nationalNumber: toNationalDigits(e164),
    };
  }

  return {
    country: parsed.country,
    nationalNumber: parsed.nationalNumber,
  };
}

/** Masks a number for display, keeping the country and the last two digits. */
export function maskPhoneNumber(e164: string): string {
  const parsed = parsePhoneNumberFromString(e164);
  if (!parsed) return e164;

  const national = parsed.nationalNumber;
  const visible = national.slice(-2);

  return `+${parsed.countryCallingCode} ${"•".repeat(Math.max(national.length - 2, 0))}${visible}`;
}
